import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';

import AddMaintenanceDialog from '../maintenances/AddMaintenanceDialog';
import AIWorkOrderDialog from '../maintenances/ai/AIWorkOrderDialog';
import QuoteMachineDialog from '../quote-machine/QuoteMachineDialog';
import CreateStandaloneInvoiceDialog from '../invoices/CreateStandaloneInvoiceDialog';
import WhatsNewDialog from '../whatsnew/WhatsNewDialog';
import { DashboardActionsContext, writeMaintenanceListPrefs } from './dashboardActions';
import { invalidate } from './storeDashboardData';

/**
 * Mounts every page-level dialog once and provides `useDashboardActions()` to the widgets.
 * Dialogs never live inside a widget, so the scaled gallery previews never contain one.
 */
export default function DashboardDialogs({ children }) {
  const navigate = useNavigate();
  const { currentOrganization, user } = useUserStore();
  const [addOpen, setAddOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPreFill, setAiPreFill] = useState(null);
  const [quoteMachineOpen, setQuoteMachineOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);

  const aiEnabled = Boolean(currentOrganization?.ai_work_order_enabled);
  const quoteMachineEnabled = Boolean(currentOrganization?.quote_machine_enabled);

  const onCreated = useCallback(() => {
    invalidate(['wo:', 'stats', 'cal:']);
  }, []);

  const handleSwitchToAi = useCallback((data) => {
    setAiPreFill(data);
    setAddOpen(false);
    setQuoteOpen(false);
    setAiOpen(true);
  }, []);

  const open = useCallback(
    (key, payload) => {
      switch (key) {
        case 'new_wo':
          setAddOpen(true);
          break;
        case 'new_quote':
          setQuoteOpen(true);
          break;
        case 'new_invoice':
          setInvoiceOpen(true);
          break;
        case 'ai_wo':
          setAiPreFill(payload || null);
          setAiOpen(true);
          break;
        case 'quote_machine':
          setQuoteMachineOpen(true);
          break;
        case 'whatsnew':
          setWhatsNewOpen(true);
          break;
        case 'counter_sale':
          navigate('/kassamyynti');
          break;
        case 'new_lahete':
          navigate('/lahetteet');
          break;
        case 'open_calendar':
          navigate('/calendar');
          break;
        case 'open_inventory':
          navigate('/inventory');
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  const navigateToMaintenancesWithFilters = useCallback(
    (overrides) => {
      writeMaintenanceListPrefs(currentOrganization, overrides);
      navigate('/maintenances');
    },
    [currentOrganization, navigate]
  );

  const value = useMemo(() => ({ open, navigateToMaintenancesWithFilters, invalidate }), [open, navigateToMaintenancesWithFilters]);

  return (
    <DashboardActionsContext.Provider value={value}>
      {children}
      <AddMaintenanceDialog
        open={addOpen}
        addCreatedMaintenance={onCreated}
        handleClose={() => setAddOpen(false)}
        onSwitchToAi={aiEnabled ? handleSwitchToAi : undefined}
      />
      <AddMaintenanceDialog
        open={quoteOpen}
        addCreatedMaintenance={onCreated}
        handleClose={() => setQuoteOpen(false)}
        initialState="quote"
        onSwitchToAi={aiEnabled ? handleSwitchToAi : undefined}
      />
      {aiEnabled ? (
        <AIWorkOrderDialog
          open={aiOpen}
          handleClose={() => {
            setAiOpen(false);
            setAiPreFill(null);
          }}
          addCreatedMaintenance={onCreated}
          vehicle={aiPreFill?.vehicle}
          customer={aiPreFill?.customer}
          initialText={aiPreFill?.text}
        />
      ) : null}
      {quoteMachineEnabled ? (
        <QuoteMachineDialog
          open={quoteMachineOpen}
          handleClose={() => setQuoteMachineOpen(false)}
          addCreatedMaintenance={onCreated}
        />
      ) : null}
      <CreateStandaloneInvoiceDialog
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        onInvoiceCreated={(invoice) => {
          setInvoiceOpen(false);
          navigate(`/invoices/${invoice.id}`);
        }}
      />
      <WhatsNewDialog open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} lastViewedAt={user?.last_whatsnew_viewed_at} />
    </DashboardActionsContext.Provider>
  );
}
